import type { ResearchJob } from '@/lib/api/endpoints/ai-research'

export interface ResearchProgressProps {
  job: ResearchJob | undefined
  isRunning: boolean
  triggerError: string | null
  onDismiss: () => void
}

const PHASE_COPY = {
  queued: 'Queued — waiting for a worker.',
  running: 'Running. Indexing the documents, then scoring.',
} as const

export function ResearchProgress({
  job,
  isRunning,
  triggerError,
  onDismiss,
}: ResearchProgressProps) {
  if (triggerError !== null) {
    return (
      <div className="canopy-research-banner failed" role="alert">
        <span>{triggerError}</span>
      </div>
    )
  }

  if (job === undefined) {
    return isRunning ? (
      <div className="canopy-research-banner" role="status">
        <span>Starting a research run…</span>
      </div>
    ) : null
  }

  if (job.status === 'failed') {
    return (
      <div className="canopy-research-banner failed" role="alert">
        <span>{job.error_message ?? 'The research run failed.'}</span>
        <button type="button" className="canopy-research-dismiss" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    )
  }

  if (job.status === 'complete') {
    return (
      <div className="canopy-research-banner complete" role="status">
        <span>Scoring complete — the scorecard and recommendations are up to date.</span>
        <button type="button" className="canopy-research-dismiss" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    )
  }

  return (
    <div className="canopy-research-banner" role="status" aria-live="polite">
      <span>
        {PHASE_COPY[job.status]}{' '}
        {job.triggered_by === 'upload'
          ? 'Typically 8–13 minutes after an upload.'
          : 'Typically 1–3 minutes.'}
      </span>
    </div>
  )
}
